import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';

const FONT_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Instrument+Sans:wght@400;500;600&display=swap');
`;

/* ─────────────────────────────────────────────────────────────────────

    VEDO HERO  —  optimized for SEO & Conversion

    Texto atualizado para reduzir repetição de "domínio" e melhorar a fluidez.
    Tamanhos de fonte ajustados e migrados para rem.

───────────────────────────────────────────────────────────────────── */

const CSS = `

:root {
  --bg:      #00151b;
  --accent:  #00adb5;
  --accent2: #00d4dd;
  --text:    #e8f4f7;
  --muted:   #4a6e78;
  --border:  rgba(0,173,181,0.18);
  /* Base de referência: 1rem = 16px */
}

/* ── Hero completo (sem busca ativa) ── */
.h-root {
  width: 100%;
  background: var(--bg);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid var(--border);
  padding: 5rem 2rem 4rem; /* 80px 32px 64px */
  transition: padding 0.35s ease, border-color 0.35s ease;
}

/* ── Hero colapsado ── */
.h-root--collapsed {
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 1.125rem 2rem; /* 18px 32px */
  border-bottom-color: rgba(0,173,181,0.25);
  box-shadow: 0 0.25rem 2rem rgba(0,0,0,0.5);
}

@media (min-width: 48.0625rem) { /* 769px */
  .h-root--collapsed {
    display: none;
  }
}

/* Canvas e Efeitos */
.h-canvas {
  position: absolute;
  inset: 0;
  width: 100%; height: 100%;
  z-index: 0;
  pointer-events: none;
  transition: opacity 0.4s ease;
}
.h-root--collapsed .h-canvas { opacity: 0; }

.h-vignette {
  position: absolute; inset: 0; z-index: 1;
  background:
    radial-gradient(ellipse 60% 50% at 50% 50%, transparent 30%, var(--bg) 100%),
    linear-gradient(to bottom, var(--bg) 0%, transparent 20%, transparent 80%, var(--bg) 100%);
  pointer-events: none;
}
.h-root--collapsed .h-vignette { display: none; }

.h-scan {
  position: absolute; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, var(--accent), transparent);
  opacity: 0; z-index: 2; pointer-events: none;
  animation: h-scanline 9s ease-in-out infinite 1.5s;
}
@keyframes h-scanline {
  0%   { top: -0.125rem; opacity: 0; }
  5%   { opacity: 0.3; }
  95%  { opacity: 0.3; }
  100% { top: 100%; opacity: 0; }
}
.h-root--collapsed .h-scan { display: none; }

/* ── Conteúdo expandido ── */
.h-expanded {
  position: relative; z-index: 10;
  width: 100%; max-width: 53.75rem; /* 860px */
  display: flex; flex-direction: column; align-items: flex-start;
  transition: opacity 0.25s ease, max-height 0.35s ease, margin 0.35s ease;
  overflow: hidden;
  max-height: 37.5rem; /* 600px */
  opacity: 1;
}
.h-root--collapsed .h-expanded {
  max-height: 0;
  opacity: 0;
  margin: 0;
  pointer-events: none;
}

/* Eyebrow - TAMANHO AUMENTADO */
.h-eyebrow {
  display: flex; align-items: center; gap: 0.75rem; /* 12px */
  margin-bottom: 1.5rem; /* 24px */
  opacity: 0;
  animation: h-up 0.5s 0.05s ease forwards;
}
.h-eyebrow-line { width: 2rem; height: 1px; background: var(--accent); flex-shrink: 0; }
.h-eyebrow-text {
  font-family: 'Instrument Sans', sans-serif;
  font-size: 0.8125rem; /* 13px */
  font-weight: 600;
  letter-spacing: 0.15em; text-transform: uppercase;
  color: var(--accent);
}

/* Título */
.h-title {
  font-family: 'Bebas Neue', sans-serif;
  font-size: clamp(2.5rem, 8vw, 6.25rem); /* 40px - 100px */
  line-height: 0.9;
  color: var(--text);
  margin-bottom: 1.5rem; /* 24px */
  opacity: 0;
  animation: h-up 0.6s 0.15s ease forwards;
}
.h-title-accent {
  display: inline;
  color: var(--accent);
  -webkit-text-stroke: 0;
}
.h-title-filled { display: block; }

/* H2 subtítulo - TAMANHO AUMENTADO */
.h-subtitle {
  font-family: 'Instrument Sans', sans-serif;
  font-size: 1.125rem; /* 18px */
  font-weight: 500;
  letter-spacing: -0.01em;
  color: #a3c9d4;
  margin-bottom: 1.5rem; /* 24px */
  line-height: 1.3;
}
@media (max-width: 40rem) { /* 640px */
  .h-subtitle { font-size: 1rem; } /* 16px */
}

/* Rule */
.h-rule {
  width: 100%; height: 1px;
  background: var(--border);
  margin-bottom: 1.5rem; /* 24px */
  opacity: 0;
  animation: h-up 0.4s 0.27s ease forwards;
}

/* Sub - TAMANHO AUMENTADO */
.h-sub {
  font-family: 'Instrument Sans', sans-serif;
  font-size: 1rem; /* 16px */
  line-height: 1.5;
  color: #8daab2;
  max-width: 36.25rem; /* 580px */
  margin-bottom: 2rem; /* 32px */
  opacity: 0;
  animation: h-up 0.5s 0.35s ease forwards;
}
/* Espaçamento correto entre o texto e o negrito */
.h-sub strong { color: var(--text); font-weight: 600; margin-left: 0.25rem; }

/* ── Search bar — elementos internos aumentados ── */
.h-search-wrap {
  position: relative; z-index: 10;
  width: 100%; max-width: 40rem; /* 640px */
  opacity: 0;
  animation: h-up 0.5s 0.44s ease forwards;
  transition: max-width 0.35s ease;
}
.h-root--collapsed .h-search-wrap {
  max-width: 100%;
  opacity: 1;
  animation: none;
}
@media (max-width: 40rem) {
  .h-search-wrap { max-width: 100%; }
}
.h-search {
  display: flex; align-items: center;
  height: 3.625rem; /* 58px */
  background: rgba(0,8,12,0.9);
  border: 1px solid var(--border);
  border-radius: 0.375rem; /* 6px */
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
}
@media (max-width: 40rem) {
  .h-search { height: 3.5rem; } /* 56px */
  .h-btn { border-radius: 0 0.375rem 0.375rem 0; }
}
.h-search:focus-within {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(0,173,181,0.1);
}
.h-root--collapsed .h-search {
  height: 3.125rem; /* 50px */
  background: rgba(0,20,28,0.95);
  border-radius: 0.375rem;
}

.h-search-prefix {
  padding: 0 1rem; /* 16px */
  font-family: 'Instrument Sans', sans-serif;
  font-size: 0.75rem; /* 12px */
  font-weight: 600;
  letter-spacing: 0.05em;
  color: var(--accent); opacity: 0.7;
  white-space: nowrap;
  border-right: 1px solid var(--border);
  height: 100%; display: flex; align-items: center; flex-shrink: 0;
}

.h-input {
  flex: 1; background: transparent; border: none; outline: none;
  padding: 0 1rem; /* 16px */
  font-family: 'Instrument Sans', sans-serif;
  font-size: 1rem; /* 16px */
  color: var(--text); min-width: 0;
}
.h-input::placeholder { color: #3a5f69; }

.h-btn {
  height: 100%; padding: 0 1.75rem; /* 28px */
  background: var(--accent); border: none; cursor: pointer;
  font-family: 'Instrument Sans', sans-serif;
  font-size: 0.8125rem; /* 13px */
  font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--bg); white-space: nowrap;
  transition: background 0.15s;
  flex-shrink: 0;
}
.h-btn:hover { background: var(--accent2); }

/* Botão limpar */
.h-clear {
  position: absolute; right: 6.25rem; /* 100px */
  top: 50%;
  transform: translateY(-50%);
  background: none; border: none; cursor: pointer;
  color: var(--muted); font-size: 1.25rem; /* 20px */
  padding: 0.25rem; /* 4px */
  opacity: 0.6; transition: opacity 0.15s;
}
.h-clear:hover { opacity: 1; }

/* Tags - TAMANHO AUMENTADO */
.h-tags {
  display: flex; flex-wrap: wrap; gap: 0.625rem; /* 10px */
  margin-top: 1.5rem; /* 24px */
  opacity: 0;
  animation: h-up 0.5s 0.54s ease forwards;
}
@media (max-width: 40rem) {
  .h-tags { justify-content: center; margin-top: 1.125rem; } /* 18px */
}
.h-root--collapsed .h-tags { display: none; }
.h-tag {
  font-family: 'Instrument Sans', sans-serif;
  font-size: 0.75rem; /* 12px */
  color: #a3c9d4;
  padding: 0.375rem 0.75rem; /* 6px 12px */
  border: 1px solid rgba(0,173,181,0.2);
  border-radius: 0.25rem; /* 4px */
  cursor: pointer;
  transition: all 0.15s;
}
.h-tag:hover {
  color: #fff;
  border-color: var(--accent);
  background: rgba(0,173,181,0.1);
}

/* Corner brackets */
.h-corner {
  position: absolute; z-index: 3; pointer-events: none;
  opacity: 0; animation: h-up 0.4s 0.7s ease forwards;
}
.h-corner--tl { top: 1.5rem; left: 1.5rem; } /* 24px */
.h-corner--br { bottom: 1.5rem; right: 1.5rem; transform: rotate(180deg); }
.h-root--collapsed .h-corner { display: none; }

/* Animations */
@keyframes h-up {
  from { opacity: 0; transform: translateY(0.875rem); } /* 14px */
  to   { opacity: 1; transform: translateY(0); }
}

/* Responsive Ajustes Finais */
@media (max-width: 40rem) {
  .h-root { padding: 3rem 1.25rem 2.5rem; } /* 48px 20px 40px */
  .h-root--collapsed { padding: 0.75rem 1rem; } /* 12px 16px */
  .h-search-prefix { display: none; }
  .h-corner { display: none; }
  .h-title { font-size: clamp(2rem, 10vw, 3rem); } /* 32px - 48px */
}
`;

/* ─── Three.js ───────────────────────────────────────────────────── */
function useScene(ref) {
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const W = canvas.clientWidth || window.innerWidth;
    const H = canvas.clientHeight || window.innerHeight;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    renderer.setSize(W, H);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, W / H, 0.1, 300);
    camera.position.set(0, 0, 40);
    const COUNT = 600;
    const positions  = new Float32Array(COUNT * 3);
    const basePosArr = new Float32Array(COUNT * 3);
    const phases     = new Float32Array(COUNT);
    for (let i = 0; i < COUNT; i++) {
      const angle  = Math.random() * Math.PI * 2;
      const radius = 16 + Math.random() * 36;
      const x = Math.cos(angle) * radius;
      const y = (Math.random() - 0.5) * 22;
      const z = Math.sin(angle) * radius * 0.3 - 14;
      positions[i*3] = basePosArr[i*3] = x;
      positions[i*3+1] = basePosArr[i*3+1] = y;
      positions[i*3+2] = basePosArr[i*3+2] = z;
      phases[i] = Math.random() * Math.PI * 2;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({ color: 0x00adb5, size: 0.18, transparent: true, opacity: 0.5, sizeAttenuation: true });
    const pts = new THREE.Points(geo, mat);
    scene.add(pts);
    [[14, 0.16, 0.06], [22, 0.26, 0.038], [30, 0.1, 0.022]].forEach(([r, tilt, op], i) => {
      const verts = [];
      for (let s = 0; s <= 200; s++) {
        const a = (s / 200) * Math.PI * 2;
        verts.push(Math.cos(a)*r, Math.sin(a)*tilt*r, Math.sin(a)*r*0.28 - 5 - i*5);
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
      scene.add(new THREE.Line(g, new THREE.LineBasicMaterial({ color: 0x00adb5, transparent: true, opacity: op })));
    });
    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    const onMove = (e) => {
      mouse.tx = (e.clientX / window.innerWidth  - 0.5) * 2;
      mouse.ty = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);
    const onResize = () => {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', onResize);
    let raf;
    const clock = new THREE.Clock();
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const t = clock.getElapsedTime();
      mouse.x += (mouse.tx - mouse.x) * 0.04;
      mouse.y += (mouse.ty - mouse.y) * 0.04;
      camera.position.x = mouse.x * 2.5;
      camera.position.y = -mouse.y * 1.5;
      const pos = geo.attributes.position.array;
      for (let i = 0; i < COUNT; i++) {
        pos[i*3+1] = basePosArr[i*3+1] + Math.sin(t * 0.35 + phases[i]) * 0.5;
      }
      geo.attributes.position.needsUpdate = true;
      pts.rotation.y = t * 0.03;
      renderer.render(scene, camera);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, [ref]);
}

/* ─── Corner SVG ─────────────────────────────────────────────────── */
const Corner = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M1 31 L1 1 L31 1" stroke="rgba(0,173,181,0.28)" strokeWidth="1"/>
    <circle cx="1" cy="1" r="1.6" fill="rgba(0,173,181,0.5)"/>
  </svg>
);

const TAGS = [
  'Domínios curtos .com.br',
  'Domínio para fintech',
  'Domínio para clínica',
  'Domínio para empresa',
  'Nomes para startup',
  'Domínios disponíveis',
];

/* ─── HeroSection ────────────────────────────────────────────────── */
const HeroSection = ({ filtroBusca = '', onFiltroChange = () => {}, forceCollapsed = false }) => {
  const [local, setLocal]       = useState(filtroBusca);
  const [collapsed, setCollapsed] = useState(false);
  const canvasRef = useRef(null);

  // Sincroniza filtro externo
  useEffect(() => { setLocal(filtroBusca); }, [filtroBusca]);

  // Colapsa via forceCollapsed (controlado pelo pai) ou quando há busca digitada
  useEffect(() => {
    setCollapsed(forceCollapsed || filtroBusca.trim().length > 0);
  }, [forceCollapsed, filtroBusca]);

  useScene(canvasRef);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (local.trim()) onFiltroChange(local.trim());
  };

  const handleClear = () => {
    setLocal('');
    onFiltroChange('');
  };

  const handleTagClick = (tag) => {
    setLocal(tag);
    onFiltroChange(tag);
  };

  return (
    <>
      <style>{FONT_IMPORT + CSS}</style>
      <section className={`h-root${collapsed ? ' h-root--collapsed' : ''}`}>
        <canvas className="h-canvas" ref={canvasRef} />
        {!collapsed && <div className="h-vignette" />}
        {!collapsed && <div className="h-scan" />}
        {!collapsed && <div className="h-corner h-corner--tl"><Corner /></div>}
        {!collapsed && <div className="h-corner h-corner--br"><Corner /></div>}

        {/* Conteúdo expandido — hero completo */}
        <div className="h-expanded">
          <div className="h-eyebrow">
            <span className="h-eyebrow-line" />
            <span className="h-eyebrow-text">Encontre e compre seu ativo premium</span>
          </div>
          <h1 className="h-title">
            <span className="h-title-filled">Domínio <span className="h-title-accent">Estratégico</span></span>
            <span className="h-title-filled">para Marca <span className="h-title-accent">Forte</span></span>
          </h1>
          <div className="h-rule" />
          <h2 className="h-subtitle">Presença digital de alto impacto</h2>
          <p className="h-sub">
            Garanta um <strong>nome exclusivo </strong> e posicione sua marca com autoridade desde o primeiro dia.
            Selecionamos <strong>oportunidades curtas e memoráveis</strong> para empresas que desejam crescimento e vantagem competitiva.
          </p>
        </div>

        {/* Search — persiste e torna-se sticky */}
        <div className="h-search-wrap">
          <form onSubmit={handleSubmit} className="h-search">
            <span className="h-search-prefix">buscar →</span>
            <input
              className="h-input"
              type="text"
              placeholder="evento, negócio, saúde..."
              value={local}
              onChange={e => setLocal(e.target.value)}
              autoComplete="off"
            />
            {collapsed && local && (
              <button type="button" className="h-clear" onClick={handleClear} aria-label="Limpar busca">×</button>
            )}
            <button type="submit" className="h-btn">Buscar</button>
          </form>
        </div>

        {/* Tags — visíveis só no estado expandido */}
        
      </section>
    </>
  );
};

export default HeroSection;
